import React from 'react';
import { type LayerConfig } from '../../../types/metadata';
import { useMapStore } from '../store/useMapStore';
import { PointSettings } from '../../../types/metadata';
import { DEFAULT_POINT_SETTINGS } from '../constants/pointSettings';

interface HeatmapLayerConfigProps {
  layer: LayerConfig;
  settings: PointSettings;
}

type HeatmapType = 'hex' | 'cloud';

const rgbToHex = (rgb: number[]): string => {
  return '#' + rgb.map(x => {
    const hex = x.toString(16).toUpperCase();
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [255, 255, 255];
};

export const HeatmapLayerConfig: React.FC<HeatmapLayerConfigProps> = ({ layer, settings }) => {
  const updatePointSettings = useMapStore((state) => state.updatePointSettings);

  const handleHeatmapTypeChange = (type: HeatmapType) => {
    updatePointSettings(layer.id, { heatmapType: type });
  };

  const handlePrimaryColorChange = (hex: string) => {
    const rgb = hexToRgb(hex);
    updatePointSettings(layer.id, { heatmapPrimaryColor: rgb });
  };

  const handleSecondaryColorChange = (hex: string) => {
    const rgb = hexToRgb(hex);
    updatePointSettings(layer.id, { heatmapSecondaryColor: rgb });
  };

  const handleStrokeColorChange = (hex: string) => {
    const rgb = hexToRgb(hex);
    updatePointSettings(layer.id, { heatmapStrokeColor: rgb });
  };

  const handleStrokeChange = (value: number) => {
    updatePointSettings(layer.id, { heatmapStroke: value });
  };

  const handleOpacityChange = (value: number) => {
    updatePointSettings(layer.id, { heatmapOpacity: value });
  };

  const handleRadiusPixelsChange = (value: number) => {
    updatePointSettings(layer.id, { heatmapRadiusPixels: value });
  };

  const handleIntensityChange = (value: number) => {
    updatePointSettings(layer.id, { heatmapIntensity: value });
  };

  const handleHeatmapThresholdChange = (value: number) => {
    updatePointSettings(layer.id, { heatmapThreshold: value });
  };

  const handleCloudThresholdChange = (value: number) => {
    updatePointSettings(layer.id, { heatmapCloudThreshold: value });
  };

  const resetToDefault = (setting: keyof PointSettings) => {
    updatePointSettings(layer.id, { [setting]: DEFAULT_POINT_SETTINGS[setting] });
  };

  return (
    <>
      <div className="marker-type-section">
        <h3>Heatmap Threshold</h3>
        <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 8px' }}>
          Point count above which the layer renders as a heatmap instead of individual points.
        </p>
        <div className="color-input-wrapper">
          <input
            type="number"
            min="0"
            step="100"
            value={settings.heatmapThreshold ?? DEFAULT_POINT_SETTINGS.heatmapThreshold}
            onChange={(e) => handleHeatmapThresholdChange(Math.max(0, Number(e.target.value)))}
            className="hex-input"
            style={{ width: '100px' }}
          />
          {(settings.heatmapThreshold ?? DEFAULT_POINT_SETTINGS.heatmapThreshold) !== DEFAULT_POINT_SETTINGS.heatmapThreshold && (
            <button
              className="set-default-btn-sidebar"
              onClick={() => resetToDefault('heatmapThreshold')}
              title="Reset to default"
              style={{ marginLeft: '8px' }}
            >
              reset to default
            </button>
          )}
        </div>
      </div>

      <hr className="section-divider" />

      <div className="marker-type-section">
        <h3>Heatmap Type</h3>
        <div className="marker-type-options">
          <label className="marker-type-option">
            <input
              type="radio"
              name="heatmapType"
              value="hex"
              checked={settings.heatmapType === 'hex'}
              onChange={(e) => handleHeatmapTypeChange(e.target.value as HeatmapType)}
            />
            <span>Hex</span>
          </label>
          <label className="marker-type-option">
            <input
              type="radio"
              name="heatmapType"
              value="cloud"
              checked={settings.heatmapType === 'cloud'}
              onChange={(e) => handleHeatmapTypeChange(e.target.value as HeatmapType)}
            />
            <span>Cloud</span>
          </label>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="marker-settings-content">
        <div className="heatmap-colors-section">
          <div className="heatmap-color-row">
            <div className="color-input-group">
              <label>Secondary</label>
              <div className="color-input-wrapper">
                <input
                  type="color"
                  value={settings.heatmapSecondaryColor ? rgbToHex(settings.heatmapSecondaryColor) : '#FFFFFF'}
                  onChange={(e) => handleSecondaryColorChange(e.target.value)}
                  className="color-picker-input"
                />
                <input
                  type="text"
                  value={settings.heatmapSecondaryColor ? rgbToHex(settings.heatmapSecondaryColor) : '#FFFFFF'}
                  onChange={(e) => handleSecondaryColorChange(e.target.value)}
                  className="hex-input"
                  maxLength={7}
                />
              </div>
            </div>
            
            <div className="color-input-group">
              <label>Primary</label>
              <div className="color-input-wrapper">
                <input
                  type="color"
                  value={rgbToHex(settings.heatmapPrimaryColor!)}
                  onChange={(e) => handlePrimaryColorChange(e.target.value)}
                  className="color-picker-input"
                />
                <input
                  type="text"
                  value={rgbToHex(settings.heatmapPrimaryColor!)}
                  onChange={(e) => handlePrimaryColorChange(e.target.value)}
                  className="hex-input"
                  maxLength={7}
                />
              </div>
            </div>
          </div>

          <div className="gradient-preview-box">
            <div 
              className="gradient-preview"
              style={{
                background: `linear-gradient(to right, rgb(${settings.heatmapSecondaryColor?.join(',') || '255,255,255'}), rgb(${settings.heatmapPrimaryColor!.join(',')}))`
              }}
            />
          </div>
        </div>

        <div className="slider-section">
          {settings.heatmapType === 'hex' && (
            <>
              <div className="slider-row">
                <label>Stroke:</label>
                <input
                  type="range"
                  min="0"
                  max="3"
                  step="1"
                  value={settings.heatmapStroke}
                  onChange={(e) => handleStrokeChange(Number(e.target.value))}
                  className="slider"
                />
                <span className="slider-value">
                  {['None', 'Small', 'Medium', 'Large'][settings.heatmapStroke!]}
                </span>
              </div>

              <div className="color-input-group" style={{ marginBottom: '16px', opacity: settings.heatmapStroke === 0 ? 0.5 : 1 }}>
                <label>Stroke Color:</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={rgbToHex(settings.heatmapStrokeColor!)}
                    onChange={(e) => handleStrokeColorChange(e.target.value)}
                    className="color-picker-input"
                    disabled={settings.heatmapStroke === 0}
                  />
                  <input
                    type="text"
                    value={rgbToHex(settings.heatmapStrokeColor!)}
                    onChange={(e) => handleStrokeColorChange(e.target.value)}
                    className="hex-input"
                    maxLength={7}
                    disabled={settings.heatmapStroke === 0}
                  />
                </div>
              </div>
            </>
          )}

          <div className="slider-row">
            <label>Opacity:</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={settings.heatmapOpacity ?? 1}
              onChange={(e) => handleOpacityChange(Number(e.target.value))}
              className="slider"
            />
            <span className="slider-value">
              {Math.round((settings.heatmapOpacity ?? 1) * 100)}%
            </span>
          </div>

          {settings.heatmapType === 'cloud' && (
            <>
              <div className="slider-row">
                <label>Cloud Radius:</label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="1"
                  value={settings.heatmapRadiusPixels ?? 30}
                  onChange={(e) => handleRadiusPixelsChange(Number(e.target.value))}
                  className="slider"
                />
                <span className="slider-value">
                  {settings.heatmapRadiusPixels ?? 30}px
                </span>
                {(settings.heatmapRadiusPixels ?? DEFAULT_POINT_SETTINGS.heatmapRadiusPixels) !== DEFAULT_POINT_SETTINGS.heatmapRadiusPixels && (
                  <button
                    className="set-default-btn-sidebar"
                    onClick={() => resetToDefault('heatmapRadiusPixels')}
                    title="Reset to default"
                    style={{ marginLeft: '8px' }}
                  >
                    reset to default
                  </button>
                )}
              </div>

              <div className="slider-row">
                <label>Cloud Intensity:</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={settings.heatmapIntensity ?? 0.75}
                  onChange={(e) => handleIntensityChange(Number(e.target.value))}
                  className="slider"
                />
                <span className="slider-value">
                  {Math.round((settings.heatmapIntensity ?? 0.75) * 100)}%
                </span>
                {(settings.heatmapIntensity ?? DEFAULT_POINT_SETTINGS.heatmapIntensity) !== DEFAULT_POINT_SETTINGS.heatmapIntensity && (
                  <button
                    className="set-default-btn-sidebar"
                    onClick={() => resetToDefault('heatmapIntensity')}
                    title="Reset to default"
                    style={{ marginLeft: '8px' }}
                  >
                    reset to default
                  </button>
                )}
              </div>

              <div className="slider-row">
                <label>Cloud Threshold:</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.001"
                  value={Math.sqrt(settings.heatmapCloudThreshold ?? 0.05)}
                  onChange={(e) => handleCloudThresholdChange(Math.pow(Number(e.target.value), 2))}
                  className="slider"
                />
                <span className="slider-value">
                  {(settings.heatmapCloudThreshold ?? 0.05).toFixed(3)}
                </span>
                {(settings.heatmapCloudThreshold ?? DEFAULT_POINT_SETTINGS.heatmapCloudThreshold) !== DEFAULT_POINT_SETTINGS.heatmapCloudThreshold && (
                  <button
                    className="set-default-btn-sidebar"
                    onClick={() => resetToDefault('heatmapCloudThreshold')}
                    title="Reset to default"
                    style={{ marginLeft: '8px' }}
                  >
                    reset to default
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
